/**
 * Audio Upload Page
 *
 * Drag-and-drop upload interface for audio files.
 */

import { Button, Card, CardBody, Progress, Spinner } from '@heroui/react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { ArrowLeft, CheckCircle, FileAudio, Upload, XCircle } from 'lucide-react';
import { useCallback, useState } from 'react';

import { useDataset } from '@/lib/api/hooks/datasets';
import { useGetUploadUrl, useConfirmUpload } from '@/lib/api/hooks/audio';

interface UploadFile {
  id: string;
  file: File;
  status: 'pending' | 'uploading' | 'confirming' | 'done' | 'error';
  progress: number;
  error?: string;
}

export const Route = createFileRoute('/datasets/$datasetId/upload')({
  component: UploadPage,
});

function UploadPage() {
  const { datasetId } = Route.useParams();
  const navigate = useNavigate();
  const { data: dataset, isLoading: isLoadingDataset } = useDataset(datasetId);
  const getUploadUrl = useGetUploadUrl(datasetId);
  const confirmUpload = useConfirmUpload(datasetId);

  const [files, setFiles] = useState<UploadFile[]>([]);
  const [isDragging, setIsDragging] = useState(false);

  const updateFile = (id: string, updates: Partial<UploadFile>) => {
    setFiles((prev) =>
      prev.map((f) => (f.id === id ? { ...f, ...updates } : f))
    );
  };

  const uploadFile = useCallback(async (uploadFile: UploadFile) => {
    try {
      updateFile(uploadFile.id, { status: 'uploading', progress: 0 });

      // Get presigned URL
      const urlData = await getUploadUrl.mutateAsync({
        filename: uploadFile.file.name,
        content_type: uploadFile.file.type || 'audio/mpeg',
        file_size: uploadFile.file.size,
      });

      if (!urlData) {
        throw new Error('Failed to get upload URL');
      }

      // Upload to S3
      updateFile(uploadFile.id, { progress: 30 });

      const uploadResponse = await fetch(urlData.upload_url, {
        method: 'PUT',
        body: uploadFile.file,
        headers: {
          'Content-Type': uploadFile.file.type || 'audio/mpeg',
        },
      });

      if (!uploadResponse.ok) {
        throw new Error('Failed to upload file');
      }

      updateFile(uploadFile.id, { status: 'confirming', progress: 70 });

      // Confirm upload
      await confirmUpload.mutateAsync(urlData.audio_id);

      updateFile(uploadFile.id, { status: 'done', progress: 100 });
    } catch (error) {
      updateFile(uploadFile.id, {
        status: 'error',
        error: error instanceof Error ? error.message : 'Upload failed',
      });
    }
  }, [getUploadUrl, confirmUpload]);

  const handleFiles = useCallback(
    (fileList: FileList) => {
      const audioFiles = Array.from(fileList).filter(
        (file) => file.type.startsWith('audio/') || file.name.match(/\.(wav|mp3|ogg|flac|m4a)$/i)
      );

      const newFiles: UploadFile[] = audioFiles.map((file) => ({
        id: `${file.name}-${Date.now()}-${Math.random()}`,
        file,
        status: 'pending',
        progress: 0,
      }));

      setFiles((prev) => [...prev, ...newFiles]);

      // Start uploads
      newFiles.forEach((f) => uploadFile(f));
    },
    [uploadFile]
  );

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragging(false);
      handleFiles(e.dataTransfer.files);
    },
    [handleFiles]
  );

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback(() => {
    setIsDragging(false);
  }, []);

  const handleFileInput = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (e.target.files) {
        handleFiles(e.target.files);
      }
    },
    [handleFiles]
  );

  const completedCount = files.filter((f) => f.status === 'done').length;
  const isUploading = files.some((f) => ['pending', 'uploading', 'confirming'].includes(f.status));

  if (isLoadingDataset) {
    return (
      <div className="flex min-h-[400px] items-center justify-center">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="container-page max-w-3xl py-6">
      <div className="mb-6">
        <Button
          variant="light"
          size="sm"
          startContent={<ArrowLeft className="h-4 w-4" />}
          onPress={() =>
            navigate({
              to: '/datasets/$datasetId',
              params: { datasetId },
              search: { tab: 'files' },
            })
          }
          className="mb-4"
        >
          Back to {dataset?.name || 'Dataset'}
        </Button>
        <h1 className="text-2xl font-bold">Upload Audio Files</h1>
        <p className="text-default-500">
          Drag and drop audio files or click to browse.
        </p>
      </div>

      {/* Drop zone */}
      <Card
        className={`mb-6 border-2 border-dashed transition-colors ${
          isDragging
            ? 'border-primary bg-primary/5'
            : 'border-default-200 hover:border-default-300'
        }`}
      >
        <CardBody
          className="flex flex-col items-center justify-center py-12"
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
        >
          <div className="rounded-full bg-default-100 p-4 mb-4">
            <Upload className="h-8 w-8 text-default-400" />
          </div>
          <p className="text-lg font-medium mb-1">Drop audio files here</p>
          <p className="text-default-500 mb-4">or click to browse</p>
          <input
            type="file"
            accept="audio/*,.wav,.mp3,.ogg,.flac,.m4a"
            multiple
            onChange={handleFileInput}
            className="hidden"
            id="file-input"
            aria-label="Upload audio files"
          />
          <Button
            as="label"
            htmlFor="file-input"
            variant="flat"
            className="cursor-pointer"
          >
            Browse Files
          </Button>
        </CardBody>
      </Card>

      {/* Upload status */}
      {files.length > 0 && (
        <Card>
          <CardBody className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold">
                Uploads ({completedCount}/{files.length})
              </h3>
              {isUploading && <Spinner size="sm" />}
            </div>

            <div className="space-y-3 max-h-80 overflow-y-auto">
              {files.map((uploadFile) => (
                <div
                  key={uploadFile.id}
                  className="flex items-center gap-3 rounded-lg border border-default-200 p-3"
                >
                  <FileAudio className="h-5 w-5 text-default-400 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="truncate text-sm font-medium">{uploadFile.file.name}</p>
                    {uploadFile.status === 'error' ? (
                      <p className="text-xs text-danger">{uploadFile.error}</p>
                    ) : (
                      <Progress
                        size="sm"
                        value={uploadFile.progress}
                        color={uploadFile.status === 'done' ? 'success' : 'primary'}
                        className="mt-1"
                        aria-label="Upload progress"
                      />
                    )}
                  </div>
                  <div className="flex-shrink-0">
                    {uploadFile.status === 'done' && (
                      <CheckCircle className="h-5 w-5 text-success" />
                    )}
                    {uploadFile.status === 'error' && (
                      <XCircle className="h-5 w-5 text-danger" />
                    )}
                    {['pending', 'uploading', 'confirming'].includes(uploadFile.status) && (
                      <Spinner size="sm" />
                    )}
                  </div>
                </div>
              ))}
            </div>

            {completedCount > 0 && !isUploading && (
              <div className="flex justify-end gap-3 pt-2">
                <Button
                  variant="flat"
                  onPress={() => setFiles([])}
                >
                  Clear
                </Button>
                <Button
                  color="primary"
                  onPress={() =>
                    navigate({
                      to: '/datasets/$datasetId',
                      params: { datasetId },
                      search: { tab: 'files' },
                    })
                  }
                >
                  View Files ({completedCount})
                </Button>
              </div>
            )}
          </CardBody>
        </Card>
      )}
    </div>
  );
}
